import { fetchApi } from "./fetch";
import { buildOnboardingPayloadFromAddress } from "../modules/containers/Auth/completeProfileOnboarding";
import { selectMainShippingAddress } from "../modules/Reducers/actions/shippingAddressUpsert";

// Shipping addresses store `country` as a full name (e.g. "France"), but Stripe
// Connect requires an ISO 3166-1 alpha-2 code (e.g. "FR"). Load the countries
// list so a stored name can be resolved to a code before it reaches Stripe.
const fetchCountryOptions = async () => {
    try {
        const response = await fetchApi("/api/shipping-methods/countries-regions");
        const list = response?.data?.allCountries || [];
        return list
            .map((country) => ({
                code: String(country.code || country.country_code || "").toUpperCase(),
                name: country.name || country.country_name || "",
            }))
            .filter((country) => country.code);
    } catch {
        return [];
    }
};

/**
 * Builds the /api/accounts/onboard payload (country + address) from the
 * user's main address. A connected account's country is immutable in Stripe,
 * so callers must NOT create an account when this returns null/no country —
 * prompt the user for their address instead of silently defaulting.
 *
 * The stored country is normalized to an ISO alpha-2 code; if it can't be
 * resolved to one (e.g. an unrecognized name), this returns null so the caller
 * blocks rather than sending an invalid country like "France" to Stripe.
 */
export const buildOnboardingPayloadFromMainAddress = async () => {
    try {
        const response = await fetchApi("/api/profile/shipping-addresses");
        const addresses = response?.success && Array.isArray(response?.data) ? response.data : [];
        const mainAddress = selectMainShippingAddress(addresses);
        if (!mainAddress) return null;

        const countries = await fetchCountryOptions();
        return buildOnboardingPayloadFromAddress(mainAddress, countries);
    } catch {
        return null;
    }
};
